import React, { FunctionComponent } from 'react';
import {
  Box,
  Container,
  IconButton,
  Link,
  Slide,
  Tooltip,
} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import { map } from 'lodash';
import HC_more from 'highcharts/highcharts-more';

import DownloadIcon from '@mui/icons-material/Download';

// Import helper utilities
import { GRADIENT_CONFIG } from './helpers/constants';
import { GenerateChartPanelProps } from './helpers/types';

import { getFormatConfig, formatValue } from './helpers/formatUtils';
import { generateTooltipHTML } from './helpers/tooltipUtils';

HC_more(Highcharts);

const GenerateChartPanel: FunctionComponent<GenerateChartPanelProps> = ({
  data,
  open,
  formatKey,
  outputName,
  blobURL,
}) => {
  // Apply colors to chart data based on values
  const colorChartByValue = <T extends { y?: number; groups?: any[] }>(
    data: T[]
  ): (T & { color: any })[] => {
    return map(data, (item: T, index: number, array: T[]) => {
      const isFirstOrLast = index === 0 || index === array.length - 1;
      const itemValue = item.y as number;

      // Determine base color based on value
      let baseColor: string;
      if (isFirstOrLast) {
        baseColor = GRADIENT_CONFIG.DEFAULT_COLOR;
      } else {
        baseColor =
          itemValue >= 0
            ? GRADIENT_CONFIG.POSITIVE_COLOR
            : GRADIENT_CONFIG.NEGATIVE_COLOR;
      }


      return {
        ...item,
        color: baseColor,
        isSum: index === array.length - 1,
      };
    });
  };

  const options: Highcharts.Options = {
    chart: {
      type: 'waterfall',
      height: '500px',
    },

    title: {
      text: '',
    },

    xAxis: {
      type: 'category',
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 'normal',
        },
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter: function () {
          const config = getFormatConfig(formatKey);
          return formatValue(this.value as number, config);
        },
        style: {
          fontSize: '13px',
        },
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      waterfall: {
        lineWidth: 3,
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const point = this.point as any;
        const config = getFormatConfig(formatKey);
        const value = formatValue(Number(this.y), config);

        // Determine if this is first or last point
        const series = this.series;
        const pointIndex = series.data.indexOf(this.point);
        const isFirstOrLast =
          pointIndex === 0 || pointIndex === series.data.length - 1;

        return generateTooltipHTML(
          outputName,
          value,
          Number(this.y),
          config,
          point.groups,
          isFirstOrLast
        );
      },
    },
    series: [
      {
        type: 'waterfall',
        pointPadding: 0,
        data: colorChartByValue(
          map(data, (d, idx) => {
            return {
              name: d.ColumnName,
              y: d.Value,
              groups: d.Groups,
            };
          })
        ),
      },
    ],
  };
  return (
    <Box>
      <Slide direction="right" in={open} mountOnEnter unmountOnExit>
        <Container
          maxWidth={false}
          sx={{
            mb: 2,
            mt: 2,
            pt: 0,
          }}
        >
          <Box
            sx={{
              justifyContent: 'end',
              display: 'flex',
              alignItems: 'center',
              paddingBottom: '12px',
            }}
          >
            <Tooltip title="Download Waterfall Report" arrow placement="left">
              <Link href={blobURL} download>
                <IconButton>
                  <DownloadIcon color="primary" />
                </IconButton>
              </Link>
            </Tooltip>
          </Box>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </Container>
      </Slide>
    </Box>
  );
};

export default GenerateChartPanel;
